import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Plus, Loader2, ChevronRight, FileOutput, Sparkles } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { CollapsibleSection } from './CollapsibleSection';
import { fmtDe, ago } from './onsite/useOnsitePlan';

const STATUS = { planning: ['in Planung', 'border-amber-500/40 bg-amber-500/10 text-amber-300'], final: ['final', 'border-teal-500/40 bg-teal-500/10 text-teal-300'], done: ['durchgeführt', 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'] };
const TEND = { go: ['Go', 'text-emerald-400'], pause: ['Pause', 'text-amber-400'], no_go: ['No-Go', 'text-red-400'] };
const inputCls = 'bg-secondary/40 border border-border rounded-sm px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:border-teal-500/60';

export const OnsitePlannerCard = ({ projectId }) => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', date: '' });
  const [creating, setCreating] = useState(false);

  useEffect(() => { api.onsiteList(projectId).then((r) => setPlans(r.data.onsites || [])).catch(() => {}); }, [projectId]);

  const create = async (prefill) => {
    setCreating(true);
    try {
      const r = await api.onsiteCreate(projectId, form);
      if (prefill) { await api.onsitePrefill(r.data.id).catch(() => {}); toast.success('On-Site-Plan angelegt — KI füllt ihn gerade aus Deal-Daten vor'); }
      else toast.success('On-Site-Plan angelegt');
      navigate(`/projects/${projectId}/onsite/${r.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Anlegen fehlgeschlagen'); }
    setCreating(false);
  };

  return (
    <CollapsibleSection storageKey={`deal.${projectId}.onsite`} icon={MapPin} iconClass="text-teal-400" title="On-Site-Planer" count={plans.length || undefined} testId="onsite-card"
      right={<button onClick={(e) => { e.stopPropagation(); setShowForm((v) => !v); }} data-testid="onsite-new-btn"
        className="inline-flex items-center gap-1.5 text-[11px] text-teal-400 hover:text-teal-300 border border-teal-900/40 rounded-sm px-2.5 py-1.5 transition-colors"><Plus className="w-3 h-3" /> Neuer On-Site</button>}>
      {showForm && (
        <div className="px-5 py-4 border-b border-border space-y-2" data-testid="onsite-form">
          <p className="text-[11px] text-muted-foreground">Legt das interne Planungsdokument aus dem OLTA-Template an (Ziele, Rollen, Ablauf, Fragenbank, Hypothesen, Logistik, Nachbereitung). Inhaber, Adresse, Team und NDA-Status werden aus dem Deal übernommen.</p>
          <div className="flex flex-wrap gap-2">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titel (optional, z. B. Erster Vor-Ort-Termin)" data-testid="onsite-form-title" className={`${inputCls} flex-1 min-w-[200px]`} />
            <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="onsite-form-date" className={inputCls} />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => create(true)} disabled={creating} data-testid="onsite-create-ai-btn"
              className="inline-flex items-center gap-1.5 text-[11px] font-medium bg-teal-700 hover:bg-teal-600 text-white rounded-sm px-3 py-2 disabled:opacity-50">
              {creating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Anlegen & KI vorbefüllen
            </button>
            <button onClick={() => create(false)} disabled={creating} data-testid="onsite-create-btn"
              className="inline-flex items-center gap-1.5 text-[11px] text-foreground border border-border rounded-sm px-3 py-2 hover:bg-secondary/50 disabled:opacity-50"><Plus className="w-3 h-3" /> Nur Template anlegen</button>
            <button onClick={() => setShowForm(false)} className="text-[11px] text-muted-foreground hover:text-foreground px-2 py-2">Abbrechen</button>
          </div>
        </div>
      )}
      {plans.length === 0 && !showForm ? (
        <p className="px-5 py-6 text-xs text-muted-foreground text-center" data-testid="onsite-empty">Noch kein Vor-Ort-Termin geplant. „Neuer On-Site“ legt das interne Planungsdokument an — daraus entsteht per Klick die externe Agenda für den Inhaber.</p>
      ) : (
        <div className="divide-y divide-border/60">
          {plans.map((p) => {
            const [sl, sc] = STATUS[p.status] || STATUS.planning;
            const t = TEND[p.tendency];
            return (
              <button key={p.id} onClick={() => navigate(`/projects/${projectId}/onsite/${p.id}`)} data-testid={`onsite-row-${p.id}`}
                className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-secondary/20 transition-colors group">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-foreground font-medium truncate">{p.title}</span>
                    <span className={`text-[9px] font-mono border rounded px-1.5 py-0.5 ${sc}`}>{sl}</span>
                    {p.external_generated_at && <span className="text-[9px] font-mono border border-sky-500/40 bg-sky-500/10 text-sky-300 rounded px-1.5 py-0.5 inline-flex items-center gap-1"><FileOutput className="w-2.5 h-2.5" /> Agenda</span>}
                    {p.prefill?.status === 'running' && <span className="text-[9px] text-violet-300 inline-flex items-center gap-1"><Loader2 className="w-2.5 h-2.5 animate-spin" /> KI füllt aus</span>}
                    {t && <span className={`text-[9px] font-mono font-bold ${t[1]}`}>{t[0]}</span>}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-x-3">
                    <span>{p.date ? `${fmtDe(p.date)}${p.start ? ` · ${p.start} Uhr` : ''}` : 'Datum offen'}</span>
                    {p.location && !p.location.includes('[') && <span>{p.location}</span>}
                    <span>Logistik {p.logistics_done}/{p.logistics_total}</span>
                    <span>Nachbereitung {p.followup_done}/{p.followup_total}</span>
                    {p.hypotheses && <span>H: {p.hypotheses.confirmed}✓ {p.hypotheses.refuted}✗ {p.hypotheses.open} offen</span>}
                    <span className="text-muted-foreground/60">zuletzt {p.updated_by_name} · {ago(p.updated_at)}</span>
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-foreground shrink-0" />
              </button>
            );
          })}
        </div>
      )}
    </CollapsibleSection>
  );
};
